/**
 * CustomerDetailSheet - 고객 상세 정보 슬라이드 패널
 *
 * 모든 페이지에서 동일한 고객 정보 UI를 제공하는 공통 컴포넌트
 * - 기본 정보 (전화번호 강조)
 * - 상담 통계
 * - 상담/예약 이력
 * - 메모 관리
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { User, Star, StarOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { UnifiedMemoPanel } from "@/components/memo/UnifiedMemoPanel";

import { CustomerInfoCard } from "./CustomerInfoCard";
import { CustomerStatsCard } from "./CustomerStatsCard";
import { CustomerHistoryCard } from "./CustomerHistoryCard";
import { formatPhoneNumber, getDisplayName } from "./utils";
import CustomerEditDialog from "./CustomerEditDialog";

import { customerApi } from "@/api/customer";
import { queryKeys } from "@/lib/queryKeys";
import type { Customer } from "@/types/customer";

export interface CustomerDetailSheetProps {
  /**
   * 표시할 고객 정보
   */
  customer: Customer | null;
  /**
   * Sheet 열림 상태
   */
  open: boolean;
  /**
   * Sheet 열림 상태 변경 핸들러
   */
  onOpenChange: (open: boolean) => void;
  /**
   * 수정 버튼 클릭 핸들러
   */
  onEdit?: (customer: Customer) => void;
  /**
   * 즐겨찾기 토글 핸들러
   */
  onToggleStar?: (id: number) => void;
  /**
   * 고객 정보 업데이트 콜백 (메모 추가/수정/삭제 후 호출)
   */
  onCustomerUpdate?: (customer: Customer) => void;
  /**
   * Sheet 너비 (sm:max-w-xl 등)
   */
  width?: string;
  /**
   * 메모 관리 표시 여부
   */
  showMemo?: boolean;
  /**
   * 상담 이력 표시 여부
   */
  showConsults?: boolean;
  /**
   * 예약 이력 표시 여부
   */
  showAppointments?: boolean;
  /**
   * 통계 표시 여부
   */
  showStats?: boolean;
  /**
   * 통화의 실제 발신번호(caller_id). 통화 맥락에서 열릴 때 전달하면, 고객 번호(customer.phone)와
   * 다를 경우 "확인 필요" 경고를 고객 정보에 표시한다. (STT 오인식으로 잘못된 번호의 고객이
   * 생성된 케이스를 담당자가 검증하도록.) 순수 고객 목록 등 통화 맥락 없는 곳에선 생략.
   */
  callerNumber?: string;
}

export function CustomerDetailSheet({
  customer,
  open,
  onOpenChange,
  onEdit,
  onToggleStar,
  onCustomerUpdate,
  width = "w-full sm:max-w-xl",
  showMemo = true,
  showConsults = true,
  showAppointments = true,
  showStats = true,
  callerNumber,
}: CustomerDetailSheetProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);

  // 고객 통계 조회
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: queryKeys.customers.stats(customer?.id),
    queryFn: () => customerApi.getCustomerStats(customer!.id),
    enabled: !!customer?.id && showStats,
  });

  // V1050: 상담 이력 조회 (10건으로 확장, totalElements로 전체 건수 표시)
  const { data: consults, isLoading: consultsLoading } = useQuery({
    queryKey: queryKeys.consults.customer(customer?.id),
    queryFn: () => customerApi.getCustomerConsults(customer!.id, 0, 10),
    enabled: !!customer?.id && showConsults,
  });

  // V1050: 예약 이력 조회 (10건으로 확장)
  const { data: appointments, isLoading: appointmentsLoading } = useQuery({
    queryKey: queryKeys.appointments.customer(customer?.id),
    queryFn: () => customerApi.getCustomerAppointments(customer!.id, 0, 10),
    enabled: !!customer?.id && showAppointments,
  });

  // 메모 데이터 변경 시 콜백
  const handleMemoDataChange = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
    queryClient.invalidateQueries({
      queryKey: queryKeys.customers.stats(customer?.id),
    });
  };

  if (!customer) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className={`${width} p-0 flex flex-col`}>
        {/* 헤더 */}
        <SheetHeader className="p-4 pb-3 border-b flex-shrink-0">
          <div className="flex items-center justify-between pr-8">
            <SheetTitle className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xl">
                    {getDisplayName(customer).name}
                  </span>
                  {getDisplayName(customer).isAi && (
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 text-violet-600 border-violet-200 bg-violet-50"
                    >
                      AI 감지
                    </Badge>
                  )}
                </div>
                <p className="text-base font-semibold text-primary">
                  {formatPhoneNumber(customer.phone)}
                </p>
              </div>
            </SheetTitle>
            <div className="flex items-center gap-2">
              {onToggleStar && (
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => onToggleStar(customer.id)}
                >
                  {customer.starred ? (
                    <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  ) : (
                    <StarOff className="w-5 h-5 text-muted-foreground" />
                  )}
                </Button>
              )}
              {onEdit ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(customer)}
                >
                  수정
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditDialogOpen(true)}
                >
                  수정
                </Button>
              )}
            </div>
          </div>
        </SheetHeader>

        {/* 본문 스크롤 영역 */}
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            {/* 기본 정보 */}
            <CustomerInfoCard customer={customer} callerNumber={callerNumber} />

            {/* 상담 통계 */}
            {showStats && (
              <CustomerStatsCard stats={stats} isLoading={statsLoading} />
            )}

            {/* 상담 이력 - V1050: customerId 전달 */}
            {showConsults && (
              <CustomerHistoryCard
                consults={consults}
                consultsLoading={consultsLoading}
                type="consults"
                customerId={customer.id}
                onConsultClick={(id) => {
                  onOpenChange(false);
                  navigate(`/consults/${id}`);
                }}
              />
            )}

            {/* 예약 이력 - V1050: customerId 전달 */}
            {showAppointments && (
              <CustomerHistoryCard
                appointments={appointments}
                appointmentsLoading={appointmentsLoading}
                type="appointments"
                customerId={customer.id}
                onAppointmentClick={(id) => {
                  onOpenChange(false);
                  navigate(`/appointments/list?appointmentId=${id}`);
                }}
              />
            )}

            {/* 고객 메모 관리 - V152 통합 메모 시스템 */}
            {showMemo && (
              <UnifiedMemoPanel
                entityType="CUSTOMER"
                entityId={customer.id}
                title="고객 메모"
                onDataChange={handleMemoDataChange}
              />
            )}
          </div>
        </ScrollArea>
      </SheetContent>

      <CustomerEditDialog
        customer={customer}
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
      />
    </Sheet>
  );
}
